// SOX Cycle Reference - Common mappings and utilities for SOX cycle assignments

// Available SOX cycles with descriptions
export const availableSoxCycles = [
  { code: 'ELC', name: 'Entity Level Controls', description: 'Organization-wide controls' },
  { code: 'FSC', name: 'Financial Statement Close Process', description: 'Financial close & consolidations' },
  { code: 'ER', name: 'External Reporting', description: 'External financial reporting' },
  { code: 'OTC', name: 'Order to Cash', description: 'Revenue and receivables' },
  { code: 'TRS', name: 'Treasury', description: 'Cash and treasury management' },
  { code: 'DBT', name: 'Debt', description: 'Debt obligations and management' },
  { code: 'PTP', name: 'Procure to Pay', description: 'Purchasing and payables' },
  { code: 'HTR', name: 'Hire to Retire', description: 'Payroll and human resources' },
  { code: 'INV', name: 'Inventory', description: 'Inventory management' },
  { code: 'TAX', name: 'Tax', description: 'Tax calculations and filings' },
  { code: 'FIX', name: 'Fixed Assets', description: 'Property and equipment' },
  { code: 'LEA', name: 'Leases', description: 'Lease accounting' },
  { code: 'EQY', name: 'Equity', description: 'Stock admin & equity' }
];

// SOX cycle mapping definitions based on financial line items
export const soxCycleMappings = {
  // Balance Sheet mappings
  'cash and cash equivalents': ['ELC', 'FSC', 'ER', 'OTC', 'TRS', 'DBT', 'PTP', 'HTR'],
  'cash & cash equivalents': ['ELC', 'FSC', 'ER', 'OTC', 'TRS', 'DBT', 'PTP', 'HTR'],
  'short term investments': ['ELC', 'FSC', 'ER', 'TRS'],
  'long term investments': ['ELC', 'FSC', 'ER', 'TRS'],
  'investments': ['ELC', 'FSC', 'ER', 'TRS'],
  'marketable securities': ['ELC', 'FSC', 'ER', 'TRS'],
  'restricted cash': ['ELC', 'FSC', 'ER', 'TRS'],
  'prepaid expenses': ['ELC', 'FSC', 'ER'],
  'other current assets': ['ELC', 'FSC', 'ER'],
  'accounts receivable': ['ELC', 'FSC', 'ER', 'OTC'],
  'inventory': ['ELC', 'FSC', 'ER', 'INV'],
  'deferred tax assets': ['ELC', 'FSC', 'ER', 'TAX'],
  'property and equipment': ['ELC', 'FSC', 'ER', 'FIX'],
  'property & equipment': ['ELC', 'FSC', 'ER', 'FIX'],
  'right of use assets': ['ELC', 'FSC', 'ER', 'LEA'],
  'operating lease right-of-use assets': ['ELC', 'FSC', 'ER', 'LEA'],
  'other assets': ['ELC', 'FSC', 'ER'],
  'accounts payable': ['ELC', 'FSC', 'ER', 'PTP'],
  'accrued': ['ELC', 'FSC', 'ER', 'PTP', 'INV', 'HTR'],
  'accrued expenses': ['ELC', 'FSC', 'ER', 'PTP', 'HTR'],
  'other current liabilities': ['ELC', 'FSC', 'ER', 'PTP', 'INV', 'HTR'],
  'debt obligations': ['ELC', 'FSC', 'ER', 'DBT'],
  'term loan': ['ELC', 'FSC', 'ER', 'DBT'],
  'operating lease': ['ELC', 'FSC', 'ER', 'LEA'],
  'lease liabilities': ['ELC', 'FSC', 'ER', 'LEA'],
  'operating lease liabilities': ['ELC', 'FSC', 'ER', 'LEA'],
  'deferred revenue': ['ELC', 'FSC', 'ER', 'OTC'],
  'other long-term liabilities': ['ELC', 'FSC', 'ER', 'HTR'],
  'other liabilities': ['ELC', 'FSC', 'ER'],
  'preferred stock': ['ELC', 'FSC', 'ER', 'EQY'],
  'common stock': ['ELC', 'FSC', 'ER', 'EQY'],
  'additional paid-in capital': ['ELC', 'FSC', 'ER', 'EQY'],
  'cumulative unrealized': ['ELC', 'FSC', 'ER', 'EQY'],
  'accumulated other comprehensive': ['ELC', 'FSC', 'ER', 'EQY'],
  'dividend': ['ELC', 'FSC', 'ER', 'EQY'],
  'accumulated deficit': ['ELC', 'FSC', 'ER', 'EQY'],
  'stockholders equity': ['ELC', 'FSC', 'ER', 'EQY'],
  'stockholders\' equity': ['ELC', 'FSC', 'ER', 'EQY'],
  'shareholders equity': ['ELC', 'FSC', 'ER', 'EQY'],
  // Income Statement mappings
  'income statement': ['ELC', 'FSC', 'ER'],
  'revenue': ['ELC', 'FSC', 'ER', 'OTC'],
  'revenues': ['ELC', 'FSC', 'ER', 'OTC'],
  'other revenue': ['ELC', 'FSC', 'ER', 'OTC', 'TRS'],
  'product': ['ELC', 'FSC', 'ER', 'OTC'],
  'license': ['ELC', 'FSC', 'ER', 'OTC'],
  'total sales': ['ELC', 'FSC', 'ER', 'OTC'],
  'cost of revenues': ['ELC', 'FSC', 'ER', 'OTC', 'PTP', 'INV'],
  'cost of revenue': ['ELC', 'FSC', 'ER', 'OTC', 'PTP', 'INV'],
  'sales & marketing': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'sales and marketing': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'research & development': ['ELC', 'FSC', 'ER', 'PTP', 'FIX', 'HTR', 'EQY'],
  'research and development': ['ELC', 'FSC', 'ER', 'PTP', 'FIX', 'HTR', 'EQY'],
  'technology': ['ELC', 'FSC', 'ER', 'PTP', 'FIX', 'HTR', 'EQY'],
  'general & administrative': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'general and administrative': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'sales': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'marketing': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'general': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'administrative': ['ELC', 'FSC', 'ER', 'PTP', 'HTR', 'EQY'],
  'interest income': ['ELC', 'FSC', 'ER', 'TRS'],
  'interest expense': ['ELC', 'FSC', 'ER', 'DBT', 'TRS'],
  'other income': ['ELC', 'FSC', 'ER', 'TRS'],
  'other expense': ['ELC', 'FSC', 'ER', 'TRS'],
  'provision for income taxes': ['ELC', 'FSC', 'ER', 'TAX'],
  'income tax': ['ELC', 'FSC', 'ER', 'TAX'],
  'net income': ['ELC', 'FSC', 'ER'],
  'net loss': ['ELC', 'FSC', 'ER']
};

/**
 * Get SOX cycles for a given line item label
 * @param {string} itemLabel - The line item label to get SOX cycles for
 * @param {array|null} customMapping - Custom SOX cycle mapping if available
 * @returns {array} Array of SOX cycle codes
 */
export const getSoxCycles = (itemLabel, customMapping = null) => {
  // If there's a custom mapping, use it
  if (customMapping && Array.isArray(customMapping) && customMapping.length > 0) {
    return customMapping;
  }

  const normalizedLabel = itemLabel.toLowerCase().trim();

  // Direct match
  if (soxCycleMappings[normalizedLabel]) {
    return soxCycleMappings[normalizedLabel];
  }

  // Partial match - check if the label contains any mapping key
  for (const [key, cycles] of Object.entries(soxCycleMappings)) {
    if (normalizedLabel.includes(key) || key.includes(normalizedLabel)) {
      return cycles;
    }
  }

  // Default mappings based on categories
  if (normalizedLabel.includes('asset')) return ['ELC', 'FSC', 'ER', 'FIX'];
  if (normalizedLabel.includes('liability') || normalizedLabel.includes('liabilities')) return ['ELC', 'FSC', 'ER', 'PTP'];
  if (normalizedLabel.includes('equity')) return ['ELC', 'FSC', 'ER', 'EQY'];
  if (normalizedLabel.includes('revenue') || normalizedLabel.includes('sales')) return ['ELC', 'FSC', 'ER', 'OTC'];
  if (normalizedLabel.includes('expense') || normalizedLabel.includes('cost')) return ['ELC', 'FSC', 'ER', 'HTR', 'PTP'];

  return ['ELC', 'FSC', 'ER']; // Default to Entity Level, Financial Statement Close, External Reporting
};

/**
 * Resolve custom SOX mapping for an income-statement row id. Prefers typed keys
 * (`is_revenue_{idx}_{slug}` / `is_expense_{idx}_{slug}`) then falls back to
 * legacy `is_{idx}_{slug}` saved before revenue/expense prefixes existed.
 * @param {Record<string, unknown>|null|undefined} customSoxMappings
 * @param {string|null|undefined} itemId
 * @returns {unknown|undefined}
 */
export function resolveIncomeStatementMappingByItemId(customSoxMappings, itemId) {
  if (!itemId || !customSoxMappings || typeof customSoxMappings !== 'object') return undefined;
  if (itemId in customSoxMappings) {
    return customSoxMappings[itemId];
  }
  const m = /^(is_revenue|is_expense)_(\d+)_(.+)$/.exec(itemId);
  if (m) {
    const legacyId = `is_${m[2]}_${m[3]}`;
    if (legacyId in customSoxMappings) {
      return customSoxMappings[legacyId];
    }
  }
  return undefined;
}

/**
 * Get SOX cycle color classes for styling
 * @param {string} code - SOX cycle code
 * @returns {string} Tailwind CSS classes for the cycle
 */
export const getSoxCycleColor = (code) => {
  const colors = {
    'ELC': 'bg-purple-100 text-purple-700 border-purple-200',
    'FSC': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'ER': 'bg-blue-100 text-blue-700 border-blue-200',
    'OTC': 'bg-green-100 text-green-700 border-green-200',
    'TRS': 'bg-teal-100 text-teal-700 border-teal-200',
    'DBT': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'PTP': 'bg-orange-100 text-orange-700 border-orange-200',
    'HTR': 'bg-pink-100 text-pink-700 border-pink-200',
    'INV': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'TAX': 'bg-red-100 text-red-700 border-red-200',
    'FIX': 'bg-lime-100 text-lime-700 border-lime-200',
    'LEA': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'EQY': 'bg-violet-100 text-violet-700 border-violet-200'
  };
  return colors[code] || 'bg-gray-100 text-gray-700 border-gray-200';
};

/**
 * Get SOX cycle name from code
 * @param {string} code - SOX cycle code
 * @returns {string} Full name of the SOX cycle
 */
export const getSoxCycleName = (code) => {
  const cycle = availableSoxCycles.find(c => c.code === code);
  return cycle ? cycle.name : code;
};

const normSoxText = (s) => String(s ?? '').trim().toLowerCase();

/**
 * Validate a user-defined SOX cycle before adding in Module 1 mapping.
 * @returns {string|null} Error message, or null if valid.
 */
export function getCustomSoxCycleValidationError(codeRaw, descriptionRaw, standardCycles, existingCustomCycles) {
  const code = String(codeRaw ?? '').trim().toUpperCase();
  const description = String(descriptionRaw ?? '').trim();
  if (!code || !description) {
    return 'Please enter both a cycle code and a name.';
  }
  const descNorm = normSoxText(description);
  const pool = [...(standardCycles || []), ...(existingCustomCycles || [])];
  for (const c of pool) {
    const nameN = normSoxText(c.name);
    const descrN = normSoxText(c.description);
    if (String(c.code ?? '').trim().toUpperCase() === code) {
      return `Cycle with ID ${code} or name ${description} already exists`;
    }
    if ((nameN && nameN === descNorm) || (descrN && descrN === descNorm)) {
      return `Cycle with ID '${code}' or name '${description}' already exists`;
    }
  }
  return null;
}
